import { useEffect, useState } from "react";
import { Bell, BellOff, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import {
  pushSupported,
  pushPermission,
  enableRestaurantPush,
  getExistingPushSubscription,
  syncRestaurantPush,
} from "../lib/push";

export default function DashboardPushBanner() {
  const [state, setState] = useState("checking"); // checking | prompt | on | denied | off
  const [busy, setBusy] = useState(false);
  const [justEnabled, setJustEnabled] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!pushSupported()) { if (active) setState("unsupported"); return; }
      if (pushPermission() === "denied") { if (active) setState("denied"); return; }
      if (pushPermission() !== "granted") { if (active) setState("prompt"); return; }
      try {
        const existing = await getExistingPushSubscription();
        if (existing) {
          await syncRestaurantPush(existing);
          if (active) setState("on");
          return;
        }
      } catch (e) { /* non-critical */ }
      if (active) setState("prompt");
    })();
    return () => { active = false; };
  }, []);

  const activate = async () => {
    setBusy(true);
    try {
      const result = await enableRestaurantPush();
      if (result === "granted") {
        setState("on");
        setJustEnabled(true);
        toast.success("تم تفعيل إشعارات الطلبات");
      } else if (result === "denied") {
        setState("denied");
      } else {
        toast.error("لم يتم تفعيل الإشعارات");
      }
    } catch (e) {
      toast.error("تعذر تفعيل الإشعارات");
    } finally {
      setBusy(false);
    }
  };

  if (state === "checking") return null;

  if (state === "unsupported") {
    const diag = {
      standalone: String(
        (typeof navigator !== "undefined" && navigator.standalone) ||
        (typeof window !== "undefined" && window.matchMedia?.("(display-mode: standalone)")?.matches) ||
        false,
      ),
      Notification: String(typeof Notification !== "undefined"),
      PushManager: String(typeof window !== "undefined" && "PushManager" in window),
      ServiceWorker: String(typeof navigator !== "undefined" && "serviceWorker" in navigator),
      manifest: document.querySelector("link[rel='manifest']")?.getAttribute("href") || "—",
      origin: window.location.origin,
    };
    return (
      <div className="mb-4 rounded-2xl border border-orange-100 bg-white p-3 text-sm text-gray-600"
        data-testid="dashboard-push-unsupported">
        <div className="flex items-start gap-2">
          <BellOff size={16} className="mt-0.5 text-gray-400" />
          <span>إشعارات الطلبات غير مدعومة في هذا المتصفح. على iPhone: افتح الموقع في Safari ثم «إضافة إلى الشاشة الرئيسية» وشغّله من الأيقونة (يتطلب iOS 16.4 أو أحدث).</span>
        </div>
        <details className="mt-2" data-testid="dashboard-push-diagnostics">
          <summary className="text-xs text-gray-500 cursor-pointer">تفاصيل تقنية</summary>
          <div className="mt-1 text-[11px] text-gray-500 space-y-0.5 break-all" dir="ltr">
            {Object.entries(diag).map(([k, v]) => <div key={k}>{k}: {v}</div>)}
          </div>
        </details>
      </div>
    );
  }

  if (state === "on") {
    if (!justEnabled) return null;
    return (
      <div className="mb-4 rounded-2xl border border-green-200 bg-green-50 p-3 flex items-center gap-2 text-green-800 font-bold text-sm"
        data-testid="dashboard-push-success">
        <CheckCircle2 size={16} /> تم تفعيل إشعارات الطلبات على هذا الجهاز
      </div>
    );
  }

  if (state === "denied") {
    return (
      <div className="mb-4 rounded-2xl border border-orange-100 bg-white p-3 flex items-start gap-2 text-sm text-gray-600"
        data-testid="dashboard-push-denied">
        <BellOff size={16} className="mt-0.5 text-gray-400" />
        <span>إشعارات المتصفح موقوفة لهذا الموقع — فعّلها من إعدادات المتصفح لهذا الموقع لتصلك تنبيهات الطلبات.</span>
      </div>
    );
  }

  return (
    <div className="mb-4 rounded-2xl border border-orange-100 bg-white card-shadow p-4" data-testid="dashboard-push-banner">
      <div className="font-bold flex items-center gap-2"><Bell size={18} className="text-orange-600" /> فعّل إشعارات الطلبات</div>
      <div className="text-sm text-gray-600 mt-1">فعّل الإشعارات عشان يوصلك تنبيه فور وصول طلب جديد أو وصول العميل.</div>
      <button onClick={activate} disabled={busy}
        className="mt-3 pill brand-grad text-white font-extrabold px-5 py-2.5 tap-target disabled:opacity-60"
        data-testid="dashboard-push-enable-btn">
        تفعيل الإشعارات
      </button>
    </div>
  );
}
